import { GoogleAdUnit } from '@/components/@google/GoogleAdUnit';
import DestinationRoutes from '@/components/destination/DestinationRoutes';
import DestionationStationListSection from '@/components/destination/DestionationStationListSection';
import Breadcrumbs from '@/components/ui/Breadcrumbs';
import { SearchForm } from '@/components/ui/SearchForm';
import VideoPlayer from '@/components/ui/VideoPlayer';
import { project } from '@/lib/project';
import {
	getAllFeaturedLocationsSlugs,
	getLocationPageData,
} from '@/lib/repository';
import { TravelAPIClient } from '@/lib/travel-api-client';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { NextSeo } from 'next-seo';
import { getCanonicalUrl, i18nRoute } from 'routes';

export default function Page({ destination, topRoutes, stations, locale }) {
	const { t } = useTranslation(['destination', 'common']);
	const pages = [
		{
			name: t('common:links.home.title'),
			href: i18nRoute('/', locale),
			locale,
		},
		{
			name: t(`common:links.tickets.title`),
			href: i18nRoute(`/tickets`, locale),
			locale,
		},
		{
			name: destination.longName,
			href: i18nRoute(`/destinations/[destinationId]`, locale, {
				destinationId: destination.id,
			}),
			current: true,
			locale: locale,
		},
	];

	const canonicalURL = getCanonicalUrl(
		'/destinations/[destinationId]',
		locale,
		{ destinationId: destination.slug }
	);
	return (
		<>
			<NextSeo
				title={t('destination:title', { destination: destination.longName })}
				description={t('destination:description', {
					destination: destination.longName,
				})}
				openGraph={{ url: canonicalURL }}
				canonical={canonicalURL}
				additionalMetaTags={[
					{
						name: 'keywords',
						content: t('destination:keywords', {
							destination: destination.shortName,
						}),
					},
				]}
			/>
			<main className="space-y-4">
				<section className="md:bg-linear-to-br relative md:from-white md:to-blue-50">
					<div className="mx-auto max-w-7xl px-4 md:py-10 lg:px-8">
						<div className="mb-2 md:mb-4">
							<h1 className="text-xl font-bold text-gray-900 sm:text-3xl sm:tracking-tight">
								{t(`destination:title`, {
									destination: destination.longName,
								})}
							</h1>
							<h2 className="text-base leading-tight text-gray-600 md:text-lg">
								{t(`destination:description`, {
									destination: destination.longName,
								})}
							</h2>
						</div>
						<SearchForm />
					</div>
				</section>
				<div className="mx-auto max-w-7xl space-y-2 px-4 md:space-y-4 lg:px-8">
					<GoogleAdUnit
						slotId={project.adSense.landing.ad1}
						className="h-[280px] w-[336px] md:h-[250px] md:w-full lg:h-[250px] lg:w-full"
					/>
					<div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
						<div className="col-span-1 space-y-4 lg:col-span-2">
							{topRoutes && (
								<section id="routes">
									{topRoutes.departure && (
										<>
											<DestinationRoutes
												destination={destination}
												routes={topRoutes.departure}
											/>
											<GoogleAdUnit
												className="h-[280px] w-[336px] md:h-[250px] md:w-full lg:h-[250px] lg:w-full"
												slotId={project.adSense.landing.ad3}
											/>
										</>
									)}
									{topRoutes.arrival && (
										<>
											<DestinationRoutes
												destination={destination}
												routes={topRoutes.arrival}
												namespace="arrivals"
											/>
											<GoogleAdUnit
												className="h-[280px] w-[336px] md:h-[250px] md:w-full lg:h-[250px] lg:w-full"
												slotId={project.adSense.landing.ad3}
											/>
										</>
									)}
								</section>
							)}
							{destination.videos && destination.videos.length > 0 && (
								<section id="videos" className="space-y-4">
									<h2 className="text-2xl font-bold tracking-tight text-gray-900 lg:text-3xl">
										{destination.shortName}: Videos
									</h2>
									<div className="scrollbar-hide relative flex w-full snap-x snap-mandatory gap-4 overflow-x-auto px-1 pb-1 pt-1">
										{destination.videos.map((video) => (
											<div className="flex-1" key={video.id}>
												<VideoPlayer
													className={'flex-1'}
													key={video.id}
													videoId={video.id}
													videoTitle={video.title}
													enableAds
													showPlayButton
												/>
											</div>
										))}
									</div>
								</section>
							)}

							{stations && stations.length > 0 && (
								<>
									<DestionationStationListSection
										destination={destination}
										stations={stations}
									/>
									<GoogleAdUnit
										className="h-[280px] w-[336px] md:h-[250px] md:w-full lg:h-[250px] lg:w-full"
										slotId={project.adSense.landing.ad3}
									/>
								</>
							)}
						</div>
						<div className="col-span-1">
							<GoogleAdUnit
								containerClassName={'sticky top-20'}
								slotId={project.adSense.landing.ad2}
								className="ml-auto mr-0 lg:h-[600px] lg:w-[300px]"
							/>
						</div>
					</div>
				</div>
			</main>
			<Breadcrumbs pages={pages} />
		</>
	);
}

export async function getStaticPaths({ locales }) {
	const locale = project.defaultLocale;
	const locations = await getAllFeaturedLocationsSlugs({
		locale,
	});

	const paths = locations
		.map((location) => {
			if (!location.translations || !location.translations[locale]) {
				return null;
			}

			return {
				params: { destinationId: location.translations[locale].slug },
				locale: locale,
			};
		})
		.filter(Boolean);

	return {
		paths: paths,
		fallback: 'blocking',
	};
}

export async function getStaticProps({ params, locale, locales }) {
	if (locale === 'default') {
		return {
			notFound: true,
		};
	}
	const { destinationId } = params;
	const travelApiClient = new TravelAPIClient();
	const [destination] = await travelApiClient.getLocationsBySlugs({
		slugs: [destinationId],
		locale,
		includeTranslations: true,
	});
	if (!destination) {
		return {
			notFound: true,
		};
	}

	if (destination.slug !== destinationId) {
		return {
			redirect: {
				permanent: true,
				destination: i18nRoute(`/destinations/[destinationId]`, locale, {
					destinationId: destination.slug,
				}),
			},
		};
	}

	const results = await getLocationPageData({
		destination,
		locale,
		locales,
	});

	if (!results) {
		return {
			notFound: true,
		};
	}

	const {
		topRoutes,
		stations,
		videos: allVideos,
		languageAlternates,
	} = results;

	const schemas = [];
	const shortVideos = allVideos
		.filter((video) => video.tags.length && video.tags.includes('short'))
		.sort((a, b) => {
			return new Date(b.publishedAt) - new Date(a.publishedAt);
		});

	const longVideos = allVideos
		.filter((video) => video.tags.length && !video.tags.includes('short'))
		.sort((a, b) => {
			return new Date(b.publishedAt) - new Date(a.publishedAt);
		});

	destination.videos = longVideos.filter(
		(video) =>
			video.tags.includes('destination') &&
			video.tags.includes(destination.slug)
	);

	destination.videos.forEach((video, index) => {
		if (index === 0) {
			video.isMain = true;
		}
		const videoSchema = {
			'@context': 'https://schema.org',
			'@type': 'VideoObject',
			name: video.title,
			description: video.description,
			thumbnailUrl: video.thumbnail,
			uploadDate: video.publishedAt,
			contentUrl: `https://www.youtube.com/watch?v=${video.id}`,
			duration: video.duration,
			embedUrl: `https://www.youtube.com/embed/${video.id}`,
			potentialAction: {
				'@type': 'SeekToAction',
				target: `https://youtu.be/${video.id}?t={seek_to_second_number}`,
				'startOffset-input': 'required name=seek_to_second_number',
			},
		};
		schemas.push(videoSchema);
	});
	destination.videos.push(...shortVideos);
	destination.videos.forEach((video, index) => {
		try {
			video.title = video.translations[locale].title;
			video.description = video.translations[locale].description;
			delete video.translations;
		} catch (error) {}
	});

	return {
		props: {
			schemas,
			topRoutes,
			stations,
			destination,
			languageAlternates,
			locale,
			...(await serverSideTranslations(locale, [
				'destination',
				'common',
				'route',
			])),
		},
	};
}
